/**
 * useReportSeries Hook
 * Manages report series state (Level 2 of 3-level architecture)
 * This is the main hook for the archive view - shows series, not individual snapshots
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { reportSeriesAPI } from '../api.js';

// Frequency display labels
export const FREQUENCY_LABELS = {
  daily: 'Quotidien',
  weekly: 'Hebdomadaire',
  monthly: 'Mensuel',
  quarterly: 'Trimestriel',
  manual: 'Manuel'
};

// Category order for display
const CATEGORY_ORDER = ['conformite', 'inventaire', 'couts', 'risques', 'mensuel', 'assurance', 'other'];

export function useReportSeries(options = {}) {
  const { autoLoad = true } = options;

  // State
  const [series, setSeries] = useState([]);
  const [pinnedSeries, setPinnedSeries] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: null,
    frequency: null
  });

  // Load series from API
  const loadSeries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportSeriesAPI.list({
        category: filters.category,
        frequency: filters.frequency,
        search: filters.search
      });

      if (response.ok) {
        setSeries(response.data || []);
        setPinnedSeries(response.pinned || []);
        setByCategory(response.byCategory || {});
      } else {
        throw new Error(response.error?.message || 'Failed to load series');
      }
    } catch (err) {
      console.error('Error loading report series:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters.category, filters.frequency, filters.search]);

  // Initial load
  useEffect(() => {
    if (autoLoad) {
      loadSeries();
    }
  }, [loadSeries, autoLoad]);

  // Sorted categories for display
  const sortedCategories = useMemo(() => {
    return CATEGORY_ORDER
      .filter(cat => byCategory[cat]?.length > 0)
      .map(cat => ({
        key: cat,
        series: byCategory[cat] || []
      }));
  }, [byCategory]);

  // Statistics
  const stats = useMemo(() => {
    const totalSnapshots = series.reduce((sum, s) => sum + (s.snapshot_count || 0), 0);
    const activeCount = series.filter(s => s.is_active).length;

    // Group by frequency
    const byFrequency = {};
    series.forEach(s => {
      byFrequency[s.frequency] = (byFrequency[s.frequency] || 0) + 1;
    });

    // Last generation time
    const lastGeneration = series.reduce((latest, s) => {
      const date = s.last_run_at ? new Date(s.last_run_at) : null;
      return date && (!latest || date > latest) ? date : latest;
    }, null);

    return {
      totalSeries: series.length,
      totalSnapshots,
      pinnedCount: pinnedSeries.length,
      activeCount,
      categoryCount: Object.keys(byCategory).length,
      byFrequency,
      lastGeneration
    };
  }, [series, pinnedSeries, byCategory]);

  // Toggle pin for a series
  const togglePin = useCallback(async (seriesId) => {
    try {
      const response = await reportSeriesAPI.togglePin(seriesId);
      if (response.ok) {
        // Update local state
        setSeries(prev => prev.map(s =>
          s.id === seriesId ? { ...s, is_pinned: response.data.is_pinned } : s
        ));
        // Reload to get proper sorting
        loadSeries();
      }
      return response;
    } catch (err) {
      console.error('Error toggling pin:', err);
      throw err;
    }
  }, [loadSeries]);

  // Run a series immediately
  const runSeries = useCallback(async (seriesId) => {
    try {
      const response = await reportSeriesAPI.run(seriesId);
      if (response.ok) {
        // Reload to get updated snapshot counts
        loadSeries();
      }
      return response;
    } catch (err) {
      console.error('Error running series:', err);
      throw err;
    }
  }, [loadSeries]);

  // Update filters
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({ search: '', category: null, frequency: null });
  }, []);

  return {
    // Data
    series,
    pinnedSeries,
    byCategory,
    sortedCategories,
    stats,
    loading,
    error,
    filters,

    // Actions
    loadSeries,
    togglePin,
    runSeries,
    updateFilter,
    resetFilters,

    // Helpers
    isPinned: (id) => pinnedSeries.some(s => s.id === id),
    getFrequencyLabel: (freq) => FREQUENCY_LABELS[freq] || freq
  };
}

export default useReportSeries;
